/* ============================================================
   Royal Perfumerie — 3D / Motion Engine (theme-3d.js)
   Vanilla JS, no dependencies except optional lazy Three.js
   for the hero bottle. Degrades gracefully on mobile / reduced
   motion / low-power devices.
   ============================================================ */
(function () {
  "use strict";

  var reduceMotion = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia &&
    window.matchMedia("(hover: none)").matches;

  /* ---------- 1. Scroll reveal ---------- */
  function initReveal() {
    var targets = document.querySelectorAll(
      ".product-card, .brand-card, .ge-section__title, .ge-promo-banner, " +
      ".ge-trust__item, .ge-dual__col, .customer-reviews .review, .rte > *"
    );
    if (!targets.length) return;
    targets.forEach(function (el) { el.classList.add("rp-reveal"); });

    if (!("IntersectionObserver" in window) || reduceMotion) {
      targets.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 2. Pointer tilt on cards ---------- */
  function initTilt() {
    if (isTouch || reduceMotion) return;
    var cards = document.querySelectorAll(".product-card, .product-page__main-img");
    var MAX = 6; // degrees
    cards.forEach(function (card) {
      card.classList.add("rp-tilt");
      var raf = null;
      function onMove(ev) {
        var r = card.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width;
        var py = (ev.clientY - r.top) / r.height;
        var rx = (0.5 - py) * MAX * 2;
        var ry = (px - 0.5) * MAX * 2;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          card.style.transform =
            "perspective(900px) rotateX(" + rx.toFixed(2) + "deg) rotateY(" +
            ry.toFixed(2) + "deg) translateZ(6px)";
        });
      }
      function reset() {
        if (raf) cancelAnimationFrame(raf);
        card.style.transform = "";
      }
      card.addEventListener("mousemove", onMove);
      card.addEventListener("mouseleave", reset);
    });
  }

  /* ---------- 3. Hero parallax ---------- */
  function initHeroParallax() {
    var hero = document.querySelector(".ge-hero");
    if (!hero || reduceMotion) return;
    var bg = hero.querySelector(".ge-hero__bg");
    var content = hero.querySelector(".ge-hero__content");

    // floating gold orbs
    var orbWrap = document.createElement("div");
    orbWrap.className = "rp-hero-orbs";
    var sizes = [120, 80, 160, 60, 100];
    for (var i = 0; i < 5; i++) {
      var o = document.createElement("span");
      o.className = "rp-hero-orb";
      var s = sizes[i];
      o.style.width = s + "px";
      o.style.height = s + "px";
      o.style.left = (8 + i * 19) + "%";
      o.style.top = (10 + (i % 3) * 28) + "%";
      o.style.animationDelay = (i * 1.3) + "s";
      o.style.opacity = (0.25 + (i % 3) * 0.12).toFixed(2);
      orbWrap.appendChild(o);
    }
    hero.insertBefore(orbWrap, hero.firstChild);

    if (isTouch) return; // skip pointer parallax on touch
    var hr = null, lastX = 0, lastY = 0, ticking = false;
    function cacheRect(){ hr = hero.getBoundingClientRect(); }
    cacheRect();
    window.addEventListener("resize", cacheRect, { passive: true });
    window.addEventListener("scroll", cacheRect, { passive: true });
    function apply(){
      ticking = false;
      if (!hr) return;
      var px = (lastX - hr.left) / hr.width - 0.5;
      var py = (lastY - hr.top) / hr.height - 0.5;
      if (bg) bg.style.transform =
        "translateZ(-120px) scale(1.18) translate(" +
        (px * 22).toFixed(1) + "px," + (py * 22).toFixed(1) + "px)";
      if (content) content.style.transform =
        "translateZ(60px) translate(" + (px * -14).toFixed(1) + "px," +
        (py * -14).toFixed(1) + "px)";
    }
    hero.addEventListener("mousemove", function (ev) {
      lastX = ev.clientX; lastY = ev.clientY;
      if (!ticking){ ticking = true; requestAnimationFrame(apply); }
    });
  }

  /* ---------- 4. Header glass on scroll ---------- */
  function initHeader() {
    var header = document.querySelector(".ge-header");
    if (!header) return;
    var ticking = false, isScrolled = false;
    function update() {
      ticking = false;
      var should = window.scrollY > 40;
      if (should !== isScrolled){
        isScrolled = should;
        header.classList.toggle("rp-scrolled", should);
      }
    }
    window.addEventListener("scroll", function(){
      if (!ticking){ ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- 5b. 2.5D image bottle (real product photo with depth) ---------- */
  function initBottle25() {
    var hero = document.querySelector(".ge-hero");
    if (!hero) return;
    var imgUrl = hero.getAttribute("data-rp-bottle-img");
    if (!imgUrl) return;

    var stage = document.createElement("div");
    stage.className = "rp-bottle25";
    stage.style.setProperty("--bottle-img", "url('" + imgUrl + "')");
    stage.style.setProperty("--bottle-mask", "url('" + imgUrl + "')");

    var inner = document.createElement("div");
    inner.className = "rp-bottle25__inner";
    inner.innerHTML =
      '<img class="rp-bottle25__img" src="' + imgUrl + '" alt="" draggable="false">' +
      '<span class="rp-bottle25__sheen"></span>';

    var reflection = document.createElement("div");
    reflection.className = "rp-bottle25__reflection";

    stage.appendChild(inner);
    stage.appendChild(reflection);
    hero.appendChild(stage);

    if (isTouch || reduceMotion) return; // static (still floats via CSS) on touch

    // cursor tilt + drag-to-rotate-ish (parallax tilt, RAF-throttled)
    var rect = null, lx = 0, ly = 0, ticking = false;
    var baseTiltX = 0, baseTiltY = 0, dragging = false, dragRot = 0, lastPX = 0;

    function cache(){ rect = stage.getBoundingClientRect(); }
    cache();
    window.addEventListener("resize", cache, { passive: true });
    window.addEventListener("scroll", cache, { passive: true });

    function apply(){
      ticking = false;
      if (!rect) return;
      var px = (lx - rect.left) / rect.width - 0.5;
      var py = (ly - rect.top) / rect.height - 0.5;
      var rotY = (px * 18) + dragRot;     // left-right tilt
      var rotX = (-py * 12);              // up-down tilt
      inner.style.transform =
        "rotateY(" + rotY.toFixed(2) + "deg) rotateX(" + rotX.toFixed(2) + "deg)";
    }

    stage.addEventListener("mousemove", function(e){
      lx = e.clientX; ly = e.clientY;
      if (!ticking){ ticking = true; requestAnimationFrame(apply); }
    });
    stage.addEventListener("mouseleave", function(){
      inner.style.transform = "";
    });

    // optional drag to add persistent spin offset
    stage.addEventListener("mousedown", function(e){
      dragging = true; lastPX = e.clientX; stage.classList.add("rp-grabbing");
      e.preventDefault();
    });
    window.addEventListener("mousemove", function(e){
      if (!dragging) return;
      dragRot += (e.clientX - lastPX) * 0.4;
      lastPX = e.clientX;
      lx = e.clientX; ly = e.clientY;
      if (!ticking){ ticking = true; requestAnimationFrame(apply); }
    });
    window.addEventListener("mouseup", function(){
      dragging = false; stage.classList.remove("rp-grabbing");
    });
  }

  /* ---------- 5. Lazy 3D floating bottle (hero) ---------- */
  function initBottle() {
    var hero = document.querySelector(".ge-hero");
    if (!hero || isTouch || reduceMotion) return;
    if (window.innerWidth < 980) return;
    // Only if explicitly enabled via data attribute to keep control
    if (hero.getAttribute("data-rp-bottle") !== "on") return;

    var stage = document.createElement("div");
    stage.className = "rp-bottle-stage";
    hero.appendChild(stage);

    var s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
    s.onload = function () { buildBottle(stage); };
    s.onerror = function () { stage.remove(); };
    document.head.appendChild(s);
  }

  function buildBottle(stage) {
    var THREE = window.THREE;
    if (!THREE) { stage.remove(); return; }
    var w = stage.clientWidth, h = stage.clientHeight;
    var scene = new THREE.Scene();
    var cam = new THREE.PerspectiveCamera(40, w / h, 0.1, 100);
    cam.position.set(0, 0, 7);
    var renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    stage.appendChild(renderer.domElement);

    var gold = new THREE.MeshStandardMaterial({
      color: 0xdaac34, metalness: 1, roughness: 0.22
    });
    var glass = new THREE.MeshStandardMaterial({
      color: 0x1c1c1c, metalness: 0.3, roughness: 0.1,
      transparent: true, opacity: 0.85
    });

    var group = new THREE.Group();
    // faceted body (octagonal for luxury feel)
    var body = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.05, 2.4, 8), glass);
    group.add(body);
    // shoulders
    var shoulder = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 1.05, 0.5, 8), glass);
    shoulder.position.y = 1.45; group.add(shoulder);
    // neck
    var neck = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.5, 16), glass);
    neck.position.y = 1.85; group.add(neck);
    // gold collar
    var collar = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.16, 16), gold);
    collar.position.y = 2.05; group.add(collar);
    // gold cap
    var cap = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, 0.6, 16), gold);
    cap.position.y = 2.4; group.add(cap);
    // atomizer top
    var atom = new THREE.Mesh(new THREE.SphereGeometry(0.18, 16, 16), gold);
    atom.position.y = 2.78; group.add(atom);
    // gold band lower
    var band = new THREE.Mesh(new THREE.CylinderGeometry(1.07, 1.07, 0.18, 8), gold);
    band.position.y = -0.2; group.add(band);
    // gold base ring
    var base = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.1, 0.22, 8), gold);
    base.position.y = -1.3; group.add(base);

    group.rotation.z = 0.04;
    scene.add(group);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    var key = new THREE.DirectionalLight(0xfff0d0, 1.6);
    key.position.set(4, 6, 5); scene.add(key);
    var rim = new THREE.DirectionalLight(0xdaac34, 1.3);
    rim.position.set(-5, 2, -3); scene.add(rim);
    var fill = new THREE.PointLight(0xffffff, 0.6, 20);
    fill.position.set(0, -2, 6); scene.add(fill);

    // ---- interaction: auto-spin 3s, then drag to rotate ----
    stage.style.pointerEvents = "auto";
    stage.style.cursor = "grab";

    var t = 0, running = true;
    var autoSpin = true;
    var autoSpinMs = 3000;
    var startTime = performance.now();
    var targetRotY = group.rotation.y;   // where we want to be
    var velocity = 0;                    // momentum after drag
    var dragging = false;
    var lastPX = 0;

    function loop() {
      if (!running) return;
      t += 0.01;
      var now = performance.now();

      if (autoSpin) {
        // gentle auto rotation for the first 3 seconds
        group.rotation.y += 0.012;
        if (now - startTime > autoSpinMs) {
          autoSpin = false;
          targetRotY = group.rotation.y;
          stage.classList.add("rp-bottle-ready");
        }
      } else if (dragging) {
        // follow handled in pointer move; nothing here
      } else {
        // momentum glide + ease toward rest, subtle idle bob only
        if (Math.abs(velocity) > 0.0001) {
          group.rotation.y += velocity;
          velocity *= 0.94; // friction
        }
      }

      group.position.y = Math.sin(t) * 0.10; // gentle float always
      renderer.render(scene, cam);
      requestAnimationFrame(loop);
    }
    loop();

    // pointer drag to rotate (after auto-spin or even during — drag overrides)
    function onDown(e) {
      dragging = true;
      autoSpin = false;
      velocity = 0;
      lastPX = (e.touches ? e.touches[0].clientX : e.clientX);
      stage.style.cursor = "grabbing";
      e.preventDefault();
    }
    function onMove(e) {
      if (!dragging) return;
      var px = (e.touches ? e.touches[0].clientX : e.clientX);
      var dx = px - lastPX;
      lastPX = px;
      var delta = dx * 0.01;
      group.rotation.y += delta;
      velocity = delta; // carry momentum on release
    }
    function onUp() {
      if (!dragging) return;
      dragging = false;
      stage.style.cursor = "grab";
    }
    stage.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    stage.addEventListener("touchstart", onDown, { passive: false });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);

    // pause when off-screen to save battery
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (e) {
        running = e[0].isIntersecting;
        if (running) loop();
      }).observe(stage);
    }
    window.addEventListener("resize", function () {
      var nw = stage.clientWidth, nh = stage.clientHeight;
      cam.aspect = nw / nh; cam.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    });
  }

  /* ---------- 6. Page-wide gold particle field ---------- */
  function initParticles() {
    if (reduceMotion) return;
    var count = isTouch ? 14 : 30;
    var wrap = document.createElement("div");
    wrap.className = "rp-particles";
    for (var i = 0; i < count; i++) {
      var p = document.createElement("span");
      p.className = "rp-particle";
      var size = 2 + Math.random() * 4;
      p.style.width = size + "px";
      p.style.height = size + "px";
      p.style.left = Math.random() * 100 + "%";
      p.style.animationDuration = (10 + Math.random() * 16) + "s";
      p.style.animationDelay = (-Math.random() * 20) + "s";
      wrap.appendChild(p);
    }
    document.body.appendChild(wrap);
  }

  /* ---------- 7. Scroll progress bar ---------- */
  function initProgress() {
    var bar = document.createElement("div");
    bar.className = "rp-progress";
    document.body.appendChild(bar);
    var ticking = false;
    function update() {
      ticking = false;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var pct = h > 0 ? (window.scrollY / h) * 100 : 0;
      bar.style.width = pct + "%";
    }
    window.addEventListener("scroll", function(){
      if (!ticking){ ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- 8. Card cursor-glow tracking ---------- */
  function initCardGlow() {
    if (isTouch || reduceMotion) return;
    document.querySelectorAll(".product-card").forEach(function (card) {
      card.addEventListener("mousemove", function (ev) {
        var r = card.getBoundingClientRect();
        card.style.setProperty("--mx", ((ev.clientX - r.left) / r.width * 100) + "%");
        card.style.setProperty("--my", ((ev.clientY - r.top) / r.height * 100) + "%");
      });
    });
  }

  /* ---------- 9. Magnetic buttons ---------- */
  function initMagnetic() {
    if (isTouch || reduceMotion) return;
    document.querySelectorAll(".btn--accent").forEach(function (btn) {
      var r = null, lx = 0, ly = 0, t = false;
      function apply(){
        t = false; if (!r) return;
        var mx = lx - r.left - r.width / 2;
        var my = ly - r.top - r.height / 2;
        btn.style.transform = "translate(" + (mx * 0.22).toFixed(1) + "px," +
          (my * 0.3).toFixed(1) + "px) translateY(-2px) scale(1.03)";
      }
      btn.addEventListener("mouseenter", function(){ r = btn.getBoundingClientRect(); });
      btn.addEventListener("mousemove", function (ev) {
        lx = ev.clientX; ly = ev.clientY;
        if (!t){ t = true; requestAnimationFrame(apply); }
      });
      btn.addEventListener("mouseleave", function () { btn.style.transform = ""; });
    });
  }

  /* ---------- 10. Alternating reveal directions ---------- */
  function decorateReveals() {
    var cards = document.querySelectorAll(".product-card.rp-reveal, .brand-card.rp-reveal");
    cards.forEach(function (el, i) {
      el.classList.add(i % 3 === 0 ? "rp-reveal--left" :
        i % 3 === 1 ? "rp-reveal--zoom" : "rp-reveal--right");
      el.style.transitionDelay = ((i % 5) * 0.06) + "s";
    });
  }

  /* ---------- Cursor glow follower ---------- */
  function initCursor() {
    if (isTouch || reduceMotion) return;
    var glow = document.createElement("div");
    glow.className = "rp-cursor";
    document.body.appendChild(glow);
    var x = 0, y = 0, cx = 0, cy = 0;
    document.addEventListener("mousemove", function (e) {
      x = e.clientX; y = e.clientY; glow.style.opacity = "1";
    });
    document.addEventListener("mouseleave", function () { glow.style.opacity = "0"; });
    (function follow() {
      cx += (x - cx) * 0.15; cy += (y - cy) * 0.15;
      glow.style.transform = "translate(" + cx + "px," + cy + "px) translate(-50%,-50%)";
      requestAnimationFrame(follow);
    })();
  }

  /* ---------- Add-to-cart ripple ---------- */
  function initRipple() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest(".product-card__atc, .btn--accent");
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      var rip = document.createElement("span");
      rip.className = "rp-ripple";
      var size = Math.max(r.width, r.height);
      rip.style.width = rip.style.height = size + "px";
      rip.style.left = (e.clientX - r.left - size / 2) + "px";
      rip.style.top = (e.clientY - r.top - size / 2) + "px";
      btn.style.position = "relative";
      btn.style.overflow = "hidden";
      btn.appendChild(rip);
      setTimeout(function () { rip.remove(); }, 600);
    });
  }

  /* ---------- 3D auto-scrolling brand marquee ---------- */
  function initMarquee() {
    var anchor = document.querySelector("[data-rp-marquee]");
    if (!anchor) return;
    var brands = (anchor.getAttribute("data-rp-marquee") || "").split(",")
      .map(function (s) { return s.trim(); }).filter(Boolean);
    if (!brands.length) {
      brands = ["Armaf", "Lattafa", "Creed", "Dior", "Rasasi", "Afnan",
        "Maison Alhambra", "Tom Ford", "YSL", "Versace"];
    }
    var all = brands.concat(brands);
    var m = document.createElement("div");
    m.className = "rp-marquee";
    var track = document.createElement("div");
    track.className = "rp-marquee__track";
    all.forEach(function (b) {
      var s = document.createElement("span");
      s.className = "rp-marquee__item";
      s.textContent = b + " \u2726";
      track.appendChild(s);
    });
    m.appendChild(track);
    anchor.appendChild(m);
  }

  /* ---------- Page-load curtain reveal ---------- */
  function initCurtain() {
    if (reduceMotion) return;
    if (sessionStorage.getItem("rp_curtain_seen")) return;
    var c = document.createElement("div");
    c.className = "rp-curtain";
    c.innerHTML =
      '<div class="rp-curtain__brand">' +
        '<img class="rp-curtain__crest" src="' + (window.rpCrestUrl || '') + '" alt="">' +
        '<div class="rp-curtain__logo">ROYAL <span>PERFUMERIE</span></div>' +
      '</div>';
    document.body.appendChild(c);
    setTimeout(function () {
      c.classList.add("is-gone");
      sessionStorage.setItem("rp_curtain_seen", "1");
      setTimeout(function () { c.remove(); }, 1200);
    }, 1400);
  }

  /* ---------- boot ---------- */
  function boot() {
    initReveal();
    decorateReveals();
    initTilt();
    initMagnetic();
    initHeroParallax();
    initHeader();
    initProgress();
    initBottle();
    initBottle25();
    initRipple();
    initMarquee();
    initCurtain();
    // Disabled for performance (were causing card lag):
    // initCardGlow();  // extra per-pixel work on every card hover
    // initParticles(); // 28 constantly-animating elements
    // initCursor();    // global mousemove RAF loop
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else { boot(); }
})();
